import { useState } from "react";
import { C, fonts } from "../theme.js";
import { SectionLabel, Field } from "./primitives.jsx";
import { LEAD_STEPS, BUDGET_OPTIONS } from "../content.js";

const EMPTY_FORM = { name: "", company: "", email: "", message: "", budget: "", website: "" };

// Same-origin POST — CloudFront routes /api/* to the contact-form Lambda behind API
// Gateway (see infra/site.tf), so no CORS config or build-time API URL is needed.
const LEADS_ENDPOINT = "/api/leads";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(form) {
  if (!form.name.trim()) return "Please enter your name.";
  if (!form.email.trim() || !EMAIL_RE.test(form.email)) return "Please enter a valid work email.";
  if (!form.message.trim()) return "Tell us a bit about what you're trying to build.";
  return null;
}

export const LeadForm = ({ accent }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [error, setError] = useState("");

  const update = (name) => (e) => setForm((f) => ({ ...f, [name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();

    // Honeypot: bots fill every field, humans never see #website (visually hidden below).
    if (form.website) {
      setStatus("success");
      return;
    }

    const validationError = validate(form);
    if (validationError) {
      setStatus("error");
      setError(validationError);
      return;
    }

    setStatus("submitting");
    setError("");
    try {
      const res = await fetch(LEADS_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          company: form.company.trim(),
          email: form.email.trim(),
          message: form.message.trim(),
          budget: form.budget,
          website: form.website,
        }),
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      setStatus("success");
      setForm(EMPTY_FORM);
    } catch {
      setStatus("error");
      setError("Something went wrong sending your message.");
    }
  };

  const submitting = status === "submitting";

  return (
    <section id="contact" style={{ background: C.bg, padding: "140px 40px" }}>
      <div
        className="lead-grid"
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "1fr 1.1fr",
          gap: 64,
          alignItems: "start",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
          <SectionLabel accent={accent}>// CONTACT</SectionLabel>
          <h2
            style={{
              margin: 0,
              fontFamily: fonts.serif,
              fontSize: "clamp(40px, 5.4vw, 68px)",
              fontWeight: 500,
              color: C.fg,
              lineHeight: 1.02,
              letterSpacing: "-0.01em",
            }}
          >
            Ready to build something{" "}
            <em style={{ color: accent, fontStyle: "italic", fontWeight: 500 }}>that ships?</em>
          </h2>
          <p
            style={{
              margin: 0,
              fontFamily: fonts.sans,
              fontSize: 15,
              lineHeight: 1.7,
              color: C.body,
              maxWidth: 480,
            }}
          >
            Tell us a bit about the project. We'll reply within one business day with next steps — usually a 30-minute scoping call.
          </p>

          <ol
            style={{
              margin: "12px 0 0",
              padding: 0,
              listStyle: "none",
              display: "flex",
              flexDirection: "column",
              gap: 14,
            }}
          >
            {LEAD_STEPS.map(([n, t, b]) => (
              <li
                key={n}
                style={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr",
                  gap: 14,
                  alignItems: "baseline",
                }}
              >
                <span
                  style={{
                    fontFamily: fonts.mono,
                    fontSize: 11,
                    letterSpacing: "0.16em",
                    padding: "3px 8px",
                    border: `1px solid ${accent}`,
                    color: accent,
                    borderRadius: 4,
                  }}
                >
                  {n}
                </span>
                <span>
                  <span style={{ fontFamily: fonts.sans, fontSize: 14, color: C.fg, fontWeight: 600 }}>{t}.</span>{" "}
                  <span style={{ fontFamily: fonts.sans, fontSize: 14, color: C.body }}>{b}</span>
                </span>
              </li>
            ))}
          </ol>

          <div
            style={{
              marginTop: 30,
              padding: "20px 22px",
              borderRadius: 10,
              background: C.card,
              border: `1px solid ${C.border}`,
              display: "flex",
              alignItems: "center",
              gap: 16,
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 999,
                background: `linear-gradient(135deg, ${accent}66, ${accent}11)`,
                border: `1px solid ${accent}55`,
                display: "grid",
                placeItems: "center",
                fontFamily: fonts.serif,
                fontSize: 20,
                fontWeight: 600,
                color: accent,
              }}
            >
              JM
            </div>
            <div>
              <div style={{ fontFamily: fonts.sans, fontSize: 14, color: C.fg, fontWeight: 600 }}>
                Javier Moreno
              </div>
              <div style={{ fontFamily: fonts.mono, fontSize: 11, color: C.body, letterSpacing: "0.04em" }}>
                FOUNDER · LEADS DISCOVERY
              </div>
            </div>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          noValidate
          style={{
            background: C.card,
            border: `1px solid ${C.border}`,
            borderRadius: 12,
            padding: 32,
            display: "flex",
            flexDirection: "column",
            gap: 18,
          }}
        >
          {status === "success" ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 14,
                padding: "60px 0",
                textAlign: "center",
              }}
            >
              <div style={{ fontFamily: fonts.serif, fontSize: 28, fontStyle: "italic", color: accent }}>
                Thanks — message sent.
              </div>
              <div style={{ fontFamily: fonts.mono, fontSize: 12, color: C.body, letterSpacing: "0.1em" }}>
                WE'LL REPLY WITHIN ONE BUSINESS DAY.
              </div>
            </div>
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <Field id="lead-name" label="Name" name="name" accent={accent} value={form.name} onChange={update("name")} required />
                <Field id="lead-company" label="Company" name="company" accent={accent} value={form.company} onChange={update("company")} />
              </div>
              <Field id="lead-email" label="Work email" name="email" type="email" accent={accent} value={form.email} onChange={update("email")} required />
              <Field id="lead-message" label="What are you trying to build?" name="message" textarea accent={accent} value={form.message} onChange={update("message")} required />

              {/* Honeypot — hidden from sighted users and screen readers, bots fill it anyway. */}
              <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}>
                <label htmlFor="lead-website">Website</label>
                <input
                  id="lead-website"
                  name="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={update("website")}
                />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <span style={{ fontFamily: fonts.mono, fontSize: 10, letterSpacing: "0.2em", color: C.faint }}>
                  BUDGET
                </span>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {BUDGET_OPTIONS.map((b) => (
                    <label
                      key={b}
                      style={{
                        fontFamily: fonts.mono,
                        fontSize: 11,
                        letterSpacing: "0.1em",
                        padding: "8px 12px",
                        border: `1px solid ${C.border}`,
                        color: C.body,
                        borderRadius: 4,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <input
                        type="radio"
                        name="budget"
                        value={b}
                        checked={form.budget === b}
                        onChange={update("budget")}
                        style={{ accentColor: accent }}
                      />
                      {b}
                    </label>
                  ))}
                </div>
              </div>

              {status === "error" && (
                <div role="alert" style={{ fontFamily: fonts.sans, fontSize: 13, color: "#e08787" }}>
                  {error} You can also email us directly at{" "}
                  <a href="mailto:hola@puntatech.dev" style={{ color: accent }}>hola@puntatech.dev</a>.
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                style={{
                  marginTop: 6,
                  alignSelf: "flex-start",
                  background: accent,
                  color: "#0a0a0a",
                  border: "none",
                  padding: "16px 28px",
                  cursor: submitting ? "default" : "pointer",
                  opacity: submitting ? 0.7 : 1,
                  fontFamily: fonts.mono,
                  fontSize: 13,
                  fontWeight: 600,
                  letterSpacing: "0.14em",
                  clipPath:
                    "polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))",
                }}
              >
                {submitting ? "SENDING…" : "SEND MESSAGE ↗"}
              </button>
              <div
                style={{
                  fontFamily: fonts.mono,
                  fontSize: 10,
                  color: C.faint,
                  letterSpacing: "0.16em",
                  marginTop: 4,
                }}
              >
                OR EMAIL HOLA@PUNTATECH.DEV DIRECTLY
              </div>
            </>
          )}
        </form>
      </div>
    </section>
  );
};
